import { useEffect, useState } from "react";
import { api } from "./lib/api";

// ── PRICES, AGAINST WHAT THEY REALLY COST (0370) ────────────────────────────────────────────────
//
// Caleb, 2026-10-24: "the second our carriers update rates we have to be able to adjust ours as well
// or we're gonna immediately go in the red." Each price beside what it was written down to cost and
// what texts have REALLY cost lately (from the provider's receipts, the API's five-minute check), the
// margin left, and the guard's settings. A text's price raises itself when the margin is gone; this is
// where a person sets it by hand, with a reason that is kept.

type Item = { item: string; unit: string; description: string; price_cents: number; cost_cents: number;
  measured_cost_cents: number | null; measured_n: number | null; measured_at: string | null; margin_cents: number;
  min_margin_cents: number; auto_raise: boolean };
type Change = { item: string; old_price_cents: number; new_price_cents: number; cost_cents: number | null; by: string; reason: string; at: string };
type Board = { items: Item[]; history: Change[] };

const c = (n: number | null | undefined) => n == null ? "—" : n >= 100 ? `$${(n / 100).toFixed(2)}` : `${+n.toFixed(3)}¢`;
const GUARDED = new Set(["text_out", "call_minute"]);

export function Prices() {
  const [b, setB] = useState<Board | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [edit, setEdit] = useState<string | null>(null);
  const load = () => api<Board>("/staff/prices").then((x) => { setB(x); setErr(null); }).catch((e) => setErr(String(e?.message ?? e)));
  useEffect(() => { load(); }, []);
  if (err) return <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm">Could not load prices: {err}</div>;
  if (!b) return null;
  return (
    <div className="rounded-xl border border-border px-4 py-3 text-sm">
      <div className="font-bold mb-0.5">Prices against what they really cost</div>
      <div className="text-muted mb-2">
        “Really costs” is from the texting provider's receipts over the last 3 days: the dearer of a plain text and a picture
        message, plus what replies cost per text sent. When that plus the minimum margin reaches the price, the price goes up by
        itself, campaigns are emailed, and you get an alert. Once sales tax has a start date, margins are what's left after the tax inside each price.
      </div>
      <table className="w-full">
        <thead><tr className="text-left text-muted text-xs uppercase">
          <th className="py-1">Item</th><th className="text-right">Price</th><th className="text-right">Written cost</th>
          <th className="text-right">Really costs</th><th className="text-right">Margin</th><th className="text-right">Min. margin</th>
          <th className="text-center">Raises itself</th><th></th>
        </tr></thead>
        <tbody>{b.items.map((i) => {
          const thin = GUARDED.has(i.item) && i.margin_cents < i.min_margin_cents;
          return [
            <tr key={i.item} className={"border-t border-border " + (thin ? "bg-red-50 text-red-800" : "")}>
              <td className="py-1.5">{i.description}<span className="text-muted"> / {i.unit}</span></td>
              <td className="text-right font-semibold">{i.price_cents === 0 ? "Free" : c(i.price_cents)}</td>
              <td className="text-right">{c(i.cost_cents)}</td>
              <td className="text-right">{i.measured_cost_cents == null ? <span className="text-muted">not enough yet</span>
                : <>{c(i.measured_cost_cents)} <span className="text-muted text-xs">({i.measured_n?.toLocaleString()} {i.item === "call_minute" ? "minutes" : "texts"})</span></>}</td>
              <td className={"text-right " + (i.margin_cents < 0 ? "font-bold text-red-700" : "")}>{c(i.margin_cents)}</td>
              <td className="text-right">{GUARDED.has(i.item) ? c(i.min_margin_cents) : "—"}</td>
              <td className="text-center">{GUARDED.has(i.item) ? (i.auto_raise ? "Yes" : "No") : "—"}</td>
              <td className="text-right"><button className="text-xs underline" onClick={() => setEdit(edit === i.item ? null : i.item)}>Change</button></td>
            </tr>,
            edit === i.item ? <tr key={i.item + "-edit"}><td colSpan={8}><Editor i={i} onDone={() => { setEdit(null); load(); }} /></td></tr> : null,
          ];
        })}</tbody>
      </table>
      {b.history.length > 0 && (<>
        <div className="font-bold mt-3 mb-1">Price changes</div>
        <table className="w-full text-xs"><tbody>
          {b.history.map((h, k) => (
            <tr key={k} className="border-t border-border">
              <td className="py-1 whitespace-nowrap">{new Date(h.at).toLocaleString("en-US", { timeZone: "America/Chicago" })}</td>
              <td>{h.item}</td><td className="whitespace-nowrap">{c(h.old_price_cents)} → <b>{c(h.new_price_cents)}</b></td>
              <td>{h.by}</td><td className="text-muted">{h.reason}</td>
            </tr>
          ))}
        </tbody></table>
      </>)}
    </div>
  );
}

function Editor({ i, onDone }: { i: Item; onDone: () => void }) {
  const [price, setPrice] = useState(String(i.price_cents));
  const [margin, setMargin] = useState(String(i.min_margin_cents));
  const [auto, setAuto] = useState(i.auto_raise);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const guarded = GUARDED.has(i.item);
  async function save() {
    setBusy(true); setErr(null);
    try {
      await api("/staff/prices", { method: "POST", body: JSON.stringify({
        item: i.item, price_cents: parseFloat(price), reason,
        ...(guarded ? { min_margin_cents: parseFloat(margin), auto_raise: auto } : {}) }) });
      onDone();
    } catch (e: any) { setErr(String(e?.message ?? e)); } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-wrap items-end gap-3 py-2">
      <label className="flex flex-col text-xs">Price (cents)<input className="ctl" style={{ width: 96 }} value={price} onChange={(e) => setPrice(e.target.value)} /></label>
      {guarded && <label className="flex flex-col text-xs">Min. margin (cents)<input className="ctl" style={{ width: 96 }} value={margin} onChange={(e) => setMargin(e.target.value)} /></label>}
      {guarded && <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Raises itself</label>}
      <label className="flex flex-col text-xs flex-1 min-w-[200px]">Why (kept with the change)<input className="ctl" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Verizon raised MMS fees Nov 1" /></label>
      <button onClick={save} disabled={busy} className="btn disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
      {err && <div className="w-full err">{err}</div>}
    </div>
  );
}
